import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { locales, LANGS, DEFAULT_LANG } from "./locales";

/**
 * Lightweight, dependency-free i18n for the Mystic app.
 *
 * - Wrap the app once with <LanguageProvider>.
 * - Read the active language with useLang().
 * - Read a namespace's strings with useTranslations("home"), which returns an
 *   object with the SAME SHAPE the components already expect for their `T` prop.
 *   Missing keys fall back to Norwegian so nothing ever renders blank.
 */

const STORAGE_KEY = "mystic_lang";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  langs: LANGS,
});

function readInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS.some((l) => l.code === saved)) return saved;
  } catch (e) {
    /* ignore */
  }
  // Fall back to the browser language if we support it.
  const nav = (window.navigator?.language || "").slice(0, 2).toLowerCase();
  if (LANGS.some((l) => l.code === nav)) return nav;
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);

  const setLang = useCallback((next) => {
    if (!LANGS.some((l) => l.code === next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Keep <html lang="..."> in sync for accessibility / SEO.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, langs: LANGS }), [lang, setLang]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}

/** Recursively merge a base object with overrides (overrides win). */
function deepMerge(base, override) {
  if (override == null) return base;
  if (base == null) return override;
  if (typeof base !== "object" || typeof override !== "object") return override;
  if (Array.isArray(base) || Array.isArray(override)) {
    return override != null ? override : base;
  }
  const out = { ...base };
  for (const key of Object.keys(override)) {
    out[key] = deepMerge(base[key], override[key]);
  }
  return out;
}

/**
 * Return the translation object for a namespace in the active language,
 * with Norwegian as the fallback for any missing keys.
 */
export function useTranslations(namespace) {
  const { lang } = useLang();
  return useMemo(() => {
    const base = (locales[DEFAULT_LANG] && locales[DEFAULT_LANG][namespace]) || {};
    const active = (locales[lang] && locales[lang][namespace]) || {};
    return deepMerge(base, active);
  }, [lang, namespace]);
}

/** Non-hook accessor for use outside React components if ever needed. */
export function getTranslations(lang, namespace) {
  const base = (locales[DEFAULT_LANG] && locales[DEFAULT_LANG][namespace]) || {};
  const active = (locales[lang] && locales[lang][namespace]) || {};
  return deepMerge(base, active);
}
